import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'lodash';
import {
  assignProps,
  assignValue,
  findMatch,
  isValidFilter,
  mapResults,
  removeItemByCode,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import { updateSingularPanels } from '@/utils/panels';
import { readUsergroupActions } from '../constants';
import {
  findUserGroup,
  findUserGroups,
  openUserGroup,
  userGroupsResult,
  userGroupUsersState,
} from './state';
import { sortByID, updateUsergroupUsersMetrics, withUserStates } from '../helper';

const toggleUsergroupSelected = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  if (!item.selected) {
    draft.userGroups[action.category].selectedUserGroups.push(item.code);
  } else {
    const selectedUserGroups = draft.userGroups[action.category].selectedUserGroups
      .filter((userGroup) => userGroup !== item.code);
    draft.userGroups[action.category].selectedUserGroups = selectedUserGroups;
  }
  item.selected = !item.selected;
});

const fetchUsergroup = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else if (!action.isNew) {
    draft.userGroups[action.category].results.push({
      code: action.code,
      readStatus: states.PENDING,
      readErrors: [],
      copy: {},
    });
  } else {
    draft.userGroups[action.category].results.push(openUserGroup(action));
  }
});

const showUsergroup = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  if (action.isNew) {
    assignProps(item, { ...userGroupUsersState, ...action.result });
    draft.newUserGroupCode = uuidv4();
  } else {
    assignProps(item, {
      ...openUserGroup({ code: action.code, ...action.result }),
      readStatus: states.SUCCESS,
      readErrors: [],
      copy: { ...action.result },
      isEditing: false,
      isNew: false,
    });
  }
});

const showUsergroupError = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeUsergroup = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  if (item) {
    draft.userGroups[action.category].results = removeItemByCode(draft.userGroups[action.category].results, item);
  }
});

const updateUsergroupPanels = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  item.panels = updateSingularPanels(item, action);
});

const toggleUsergroupsSelected = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const fetchUsergroups = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
});

const showUsergroups = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
  item.results = action.append
    ? mapResults(item.results, action, userGroupsResult(item.selectedUserGroups))
    : mapResults([], action, userGroupsResult(item.selectedUserGroups));
});

const showUsergroupsMetrics = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results.forEach((result) => {
    const updatedResult = findMatch(action.results, result.code);
    if (updatedResult?.metrics && !isEqual(updatedResult?.metrics, result.metrics)) {
      assignValue(result, 'metrics', updatedResult.metrics);
    }
  });
});

const showUsergroupsError = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const updateUsergroupsFilter = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.filter = action.value;
});

const toggleUsergroupsPresentation = withDraft((draft, action) => {
  const item = findUserGroups(draft, action);
  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const updateUsergroupSearchFilter = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  item.userSearch.filter = action.filter;
});

const fetchUsergroupSearchUsers = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);

  if (isValidFilter(action.filter, { requiresFilter: true, minFilterLength: 3 })) {
    item.userSearch.readStatus = states.PENDING;
    item.userSearch.readErrors = [];
  }
});

const showUsergroupSearchUsers = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  item.userSearch.readStatus = states.SUCCESS;
  item.userSearch.readErrors = [];
  item.userSearch.results = action.results;
});

const showUsergroupSearchUsersError = withDraft((draft, action) => {
  const item = findUserGroup(draft, action);
  item.userSearch.readStatus = states.ERROR;
  item.userSearch.readErrors = [responseError(action)];
});

const showClonedUserGroup = withDraft((draft, {
  category,
  code,
  response,
  items,
}) => {
  const now = Date.now();
  const item = findUserGroup(draft, { category, code });

  assignProps(item, {
    ...userGroupUsersState,
    ...response,
    code: item.code,
    created_at: now,
    isDirty: true,
    name: '',
    updated_at: now,
    users: {
      results: items.map(withUserStates).sort(sortByID),
      readStatus: states.SUCCESS,
      readErrors: [],
    },
  });

  updateUsergroupUsersMetrics(draft, item);
});

export default {
  [readUsergroupActions.CLOSE_USERGROUP]: closeUsergroup,
  [readUsergroupActions.FETCH_USERGROUP]: fetchUsergroup,
  [readUsergroupActions.FETCH_USERGROUP_USERS_SEARCH]: fetchUsergroupSearchUsers,
  [readUsergroupActions.FETCH_USERGROUPS]: fetchUsergroups,
  [readUsergroupActions.FETCH_USERGROUPS_METRICS]: fetchUsergroups,
  [readUsergroupActions.SHOW_CLONED_USERGROUP]: showClonedUserGroup,
  [readUsergroupActions.SHOW_USERGROUP]: showUsergroup,
  [readUsergroupActions.SHOW_USERGROUP_ERROR]: showUsergroupError,
  [readUsergroupActions.SHOW_USERGROUP_USERS_SEARCH]: showUsergroupSearchUsers,
  [readUsergroupActions.SHOW_USERGROUP_USERS_SEARCH_ERROR]: showUsergroupSearchUsersError,
  [readUsergroupActions.SHOW_USERGROUPS]: showUsergroups,
  [readUsergroupActions.SHOW_USERGROUPS_ERROR]: showUsergroupsError,
  [readUsergroupActions.SHOW_USERGROUPS_METRICS]: showUsergroupsMetrics,
  [readUsergroupActions.TOGGLE_USERGROUP_SELECTED]: toggleUsergroupSelected,
  [readUsergroupActions.TOGGLE_USERGROUPS_PRESENTATION]: toggleUsergroupsPresentation,
  [readUsergroupActions.TOGGLE_USERGROUPS_SELECTED]: toggleUsergroupsSelected,
  [readUsergroupActions.UPDATE_USERGROUP_PANELS]: updateUsergroupPanels,
  [readUsergroupActions.UPDATE_USERGROUPS_FILTER]: updateUsergroupsFilter,
  [readUsergroupActions.UPDATE_USERGROUP_USERS_SEARCH_FILTER]: updateUsergroupSearchFilter,
};
